import { SagaIterator } from "redux-saga";
import { call, select } from "typed-redux-saga";

import { Relationship } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { TreeEngineOperation } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/client";
import {
	createA12DataServiceDataLoader,
	DataOperation,
	ProvideDataParams
} from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/server-connector";

import { getFVManifests } from "../../app/slices/fachverfahren/selectors";
import { isDocumentWithRoot } from "../../utilities/loadAppmodelTypeguards";
import { deleteDocument, getDocument, modifyDocument } from "../../utilities/rpcHelper";
import { TEXTBAUSTEINE_VERFAHREN_DOCUMENT } from "../../utilities/variables/documentModelName";

import { getVirtualVerfahrenDocRef } from "./textbausteinDataProviderHelpers";

import LinkPosition = Relationship.LinkPosition;

const { provideData } = createA12DataServiceDataLoader({ addLink: { newLinkPosition: LinkPosition.BOTTOM } });

/**
 * Verarbeitet die Mutationen und teilt sie in zwei Arrays auf. Mutationen, die mit der virtuellen Wurzel gemacht werden,
 * werden von einer eigenen Funktionalität behandelt und alles andere wird durch das Standardverhalten der tree engine behandelt.
 * @param params
 * @returns
 */
export function* handleMutations(
	params: ProvideDataParams
): SagaIterator<(TreeEngineOperation.Done | TreeEngineOperation.Failed)[] | undefined> {
	const { mutations } = params;

	const mutationResults: (TreeEngineOperation.Done | TreeEngineOperation.Failed)[] = [];

	if (!mutations) {
		return undefined;
	}

	const customMutations: TreeEngineOperation.Mutation[] = [];
	const defaultMutations: TreeEngineOperation.Mutation[] = [];

	for (const mutation of mutations) {
		if (mutation.type === "ADD_LINK" && mutation.payload.parent.docRef.includes(TEXTBAUSTEINE_VERFAHREN_DOCUMENT)) {
			customMutations.push(mutation);
		} else if (
			mutation.type === "DELETE_NODE" &&
			mutation.payload.nodePath.length >= 1 &&
			mutation.payload.nodePath[0].id.includes(TEXTBAUSTEINE_VERFAHREN_DOCUMENT)
		) {
			customMutations.push(mutation);
		} else {
			defaultMutations.push(mutation);
		}
	}

	if (customMutations.length !== 0) {
		yield* call(handleCustomMutations, mutations);
	}

	if (defaultMutations.length !== 0) {
		// add possible typecheck
		const defaultMutationResults = (yield* call(provideData, {
			...params,
			queries: [],
			mutations: defaultMutations
		})) as DataOperation.ResultSet;
		defaultMutationResults.mutationResults?.forEach(result => mutationResults.push(result));
	}

	return mutations ? mutationResults : undefined;
}

/**
 * Wird für die Erstellung eines Knotens unter einem virtuellen Knoten verwendet. Bearbeitet auch die Löschung von Knoten unter virtuellen Knoten.
 * @param mutations
 */
function* handleCustomMutations(mutations: TreeEngineOperation.Mutation[]): SagaIterator<void> {
	const manifests = yield* select(getFVManifests);

	for (const mutation of mutations) {
		if (mutation.type === "ADD_LINK") {
			const docRef = mutation.payload.child.docRef;

			const manifest = manifests
				.filter(e => !!e.textbausteinData)
				.find(manifest => getVirtualVerfahrenDocRef(manifest) === docRef);

			const marker = manifest?.textbausteinData?.marker;

			if (!marker) {
				continue;
			}

			const foundChildDocument = yield* call(getDocument, docRef);
			if (!foundChildDocument) {
				continue;
			}

			if (isDocumentWithRoot(foundChildDocument.document)) {
				const documentWithCorrelatedFachverfahrenKey = {
					root: {
						...foundChildDocument.document.root,
						correlatedFachverfahrenKey: marker
					}
				};

				yield* call(modifyDocument, foundChildDocument.docRef, documentWithCorrelatedFachverfahrenKey);
			}
		} else if (mutation.type === "DELETE_NODE") {
			yield* call(deleteDocument, mutation.payload.nodeIdentifier.id);
		}
	}
}
